// @ts-check
/**
 * @file api/_call.js
 * @location src/integrations/supabase/api/_call.js
 * @role RPC 호출 공통 헬퍼 — tasks/members/templates 가 공유.
 *
 *  callRpc(fn, args, mapper)  — 단일 row(또는 null) 반환. mapper 로 도메인 모델 변환
 *  callRpcId(fn, args, id)    — 결과 행 없이 id 만 에코(delete/purge 류)
 *
 *  신뢰성:
 *    - retry 3회 + 지수백오프 (1s, 2s, 4s) — **일시적 에러만**
 *    - 결정적 에러(PG raise P0001·제약·권한 등)는 재시도해도 같은 실패 → 즉시 throw
 *      (예: member_cap_reached 가 7초 지연 후 표시되던 문제 방지)
 *    - timeout 10초 per attempt — Promise.race
 *    - 모든 retry 실패 시 마지막 error throw
 *
 *  호출 측(useSupabaseStore.wrappedAct) 이 최종 throw 를 catch — outbox 에 적재.
 */
import { supabase } from '../client.js';

const MAX_RETRIES  = 3;
const TIMEOUT_MS   = 10_000;
const BACKOFF_MS   = [1000, 2000, 4000];

// 일시적(재시도 가치 있음) PG SQLSTATE 클래스: 08 connection · 53 insufficient_resources · 57 operator_intervention.
// 그 외 코드(P0001 raise·23* 제약·42501 권한 등)는 결정적 → 재시도 무의미. 코드 없음(네트워크/timeout)도 일시적.
const TRANSIENT_PG_CLASSES = new Set(['08', '53', '57']);

/** @param {any} error @returns {boolean} 재시도할 가치가 있는 일시적 에러인가 */
export function isTransientError(error) {
  const code = error?.code;
  if (!code || typeof code !== 'string') return true;          // 네트워크·timeout·fetch 실패 → 재시도
  if (code === '40001' || code === '40P01') return true;       // serialization failure / deadlock
  return TRANSIENT_PG_CLASSES.has(code.slice(0, 2));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout(promise, ms) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('RPC timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

async function rpcWithRetry(fn, args) {
  let lastError;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const { data, error } = await withTimeout(supabase.rpc(fn, args), TIMEOUT_MS);
      if (error) throw error;
      return data;
    } catch (e) {
      lastError = e;
      if (!isTransientError(e)) throw e;                     // 결정적 에러 → 백오프 없이 즉시 throw
      if (attempt < MAX_RETRIES - 1) await sleep(BACKOFF_MS[attempt]);
    }
  }
  throw lastError;
}

export async function callRpc(fn, args, mapper) {
  const data = await rpcWithRetry(fn, args);
  return data ? mapper(data) : null;
}

export async function callRpcId(fn, args, id) {
  await rpcWithRetry(fn, args);
  return id;
}
